import {
  CohortPatientOverview,
  PatientProgress,
  ReportSiteUse
} from '../types/domain';
import { formatLongDate, formatSiteUse } from './monthlyReport';

/**
 * FR-14 presentation helpers for the doctor / admin dashboard.
 *
 * Same division of labour as `monthlyReport.ts`: every *figure* here already
 * arrived from `doctor_patient_overview()` or `patient_progress()` — this file
 * only ranks, labels and shapes them. A number computed here would be a number
 * only the browser that drew it has ever seen, and two doctors looking at the
 * same child would have no guarantee of agreeing.
 *
 * The one thing that is genuinely decided here is *triage*: which of those
 * server-computed numbers is worth interrupting a doctor about. That is a
 * presentation rule, it changes with clinic practice, and it is cheap to
 * correct without regenerating anything.
 */

/** What a flag is about. Ordered by how urgently a clinician wants to see it. */
export type AlertKind = 'hypo' | 'hyper' | 'inactive' | 'lipodystrophy' | 'no-data';

export type AlertSeverity = 'urgent' | 'watch';

export interface PatientAlert {
  kind: AlertKind;
  severity: AlertSeverity;
  /** Indonesian, ready to render. */
  label: string;
  detail: string;
}

export interface FlaggedPatient {
  patient: CohortPatientOverview;
  alerts: PatientAlert[];
  /** Highest severity across the alerts, for sorting and for the badge. */
  severity: AlertSeverity;
}

/**
 * Triage rules.
 *
 * Deliberately blunt and deliberately few. The screen's job is to stop a
 * doctor scrolling past the child who needs them, not to grade forty children
 * on a scale — a dashboard that flags everybody flags nobody.
 *
 * A single hypo is already `watch`: below-target in a child on insulin is the
 * one direction with an immediate floor under it, which is why FR-14 asks for
 * a hypoglycemia alarm by name and not a hyperglycemia one.
 */
export const ALERT_RULES = {
  /** Hypos in the window that make it urgent rather than worth a look. */
  hypoUrgentCount: 3,
  /** Hypers in the window before it is worth mentioning at all. */
  hyperWatchCount: 5,
  hyperUrgentCount: 12,
  /** Days with no entry of any kind before the account counts as gone quiet. */
  inactiveWatchDays: 5,
  inactiveUrgentDays: 14,
  /** Lipodystrophy reports in the window before rotation needs a conversation. */
  lipodystrophyWatchCount: 1
} as const;

const RANK: Record<AlertSeverity, number> = { urgent: 2, watch: 1 };

const KIND_ORDER: AlertKind[] = [
  'hypo',
  'no-data',
  'inactive',
  'hyper',
  'lipodystrophy'
];

/**
 * Every flag this patient earns, most urgent first.
 *
 * Returns an empty array for a patient doing fine — an empty result is the
 * normal case and the caller filters on it.
 */
export function alertsFor(row: CohortPatientOverview): PatientAlert[] {
  const alerts: PatientAlert[] = [];

  if (row.hypoCount > 0) {
    alerts.push({
      kind: 'hypo',
      severity: row.hypoCount >= ALERT_RULES.hypoUrgentCount ? 'urgent' : 'watch',
      label: `${row.hypoCount}× hipoglikemia`,
      detail:
        row.lastHypoDate === null
          ? `dalam ${row.windowDays} hari terakhir`
          : `terakhir ${formatLongDate(row.lastHypoDate)}`
    });
  }

  if (row.hyperCount >= ALERT_RULES.hyperWatchCount) {
    alerts.push({
      kind: 'hyper',
      severity: row.hyperCount >= ALERT_RULES.hyperUrgentCount ? 'urgent' : 'watch',
      label: `${row.hyperCount}× gula darah sangat tinggi`,
      detail: `dari ${row.readings} kali cek dalam ${row.windowDays} hari terakhir`
    });
  }

  // "Never recorded anything" and "stopped recording" are different problems:
  // the first is an onboarding failure, the second is a child who dropped off.
  if (row.lastEntryDate === null) {
    alerts.push({
      kind: 'no-data',
      severity: 'watch',
      label: 'Belum ada catatan',
      detail: 'akun ini belum pernah mencatat apa pun'
    });
  } else if (
    row.daysSinceEntry !== null &&
    row.daysSinceEntry >= ALERT_RULES.inactiveWatchDays
  ) {
    alerts.push({
      kind: 'inactive',
      severity:
        row.daysSinceEntry >= ALERT_RULES.inactiveUrgentDays ? 'urgent' : 'watch',
      label: `Tidak mencatat ${row.daysSinceEntry} hari`,
      detail: `catatan terakhir ${formatLongDate(row.lastEntryDate)}`
    });
  }

  if (row.lipodystrophyCount >= ALERT_RULES.lipodystrophyWatchCount) {
    alerts.push({
      kind: 'lipodystrophy',
      severity: 'watch',
      label: `${row.lipodystrophyCount} laporan lipodistrofi`,
      detail: `${row.distinctSites} titik suntik berbeda dipakai`
    });
  }

  return alerts.sort((a, b) => {
    const bySeverity = RANK[b.severity] - RANK[a.severity];
    if (bySeverity !== 0) return bySeverity;
    return KIND_ORDER.indexOf(a.kind) - KIND_ORDER.indexOf(b.kind);
  });
}

/**
 * The patients worth interrupting a doctor about, most urgent first.
 *
 * Ties break on hypo count and then on name, so the order is stable across
 * reloads — a list that reshuffles under the cursor is worse than no order.
 */
export function flagPatients(rows: CohortPatientOverview[]): FlaggedPatient[] {
  return rows
    .map(patient => ({ patient, alerts: alertsFor(patient) }))
    .filter(entry => entry.alerts.length > 0)
    .map(entry => ({ ...entry, severity: entry.alerts[0].severity }))
    .sort((a, b) => {
      const bySeverity = RANK[b.severity] - RANK[a.severity];
      if (bySeverity !== 0) return bySeverity;
      const byHypo = b.patient.hypoCount - a.patient.hypoCount;
      if (byHypo !== 0) return byHypo;
      return (a.patient.fullName ?? '').localeCompare(b.patient.fullName ?? '');
    });
}

/** Cohort-level counters for the header strip. */
export function cohortSummary(rows: CohortPatientOverview[]): {
  patients: number;
  withHypo: number;
  inactive: number;
  recordingToday: number;
  averageCompliance: number;
} {
  const inactive = rows.filter(
    r => r.lastEntryDate === null || (r.daysSinceEntry ?? 0) >= ALERT_RULES.inactiveWatchDays
  ).length;
  const compliance = rows.reduce((sum, r) => sum + r.compliancePercent, 0);
  return {
    patients: rows.length,
    withHypo: rows.filter(r => r.hypoCount > 0).length,
    inactive,
    recordingToday: rows.filter(r => r.daysSinceEntry === 0).length,
    averageCompliance: rows.length ? Math.round(compliance / rows.length) : 0
  };
}

export type CohortSortField =
  | 'name'
  | 'hypo'
  | 'inTarget'
  | 'compliance'
  | 'insulin'
  | 'hba1c'
  | 'lastEntry';

/**
 * Sorts the roster.
 *
 * Every numeric column sorts worst-first, because the reason to sort a patient
 * list by "in target %" is to find the bottom of it. Missing values sink: a
 * child with no HbA1c on file is not the most urgent HbA1c in the clinic.
 */
export function sortCohort(
  rows: CohortPatientOverview[],
  field: CohortSortField
): CohortPatientOverview[] {
  const byName = (a: CohortPatientOverview, b: CohortPatientOverview) =>
    (a.fullName ?? '').localeCompare(b.fullName ?? '');

  const worstFirst = (
    value: (row: CohortPatientOverview) => number | null,
    direction: 'asc' | 'desc'
  ) => (a: CohortPatientOverview, b: CohortPatientOverview) => {
    const av = value(a);
    const bv = value(b);
    if (av === null && bv === null) return byName(a, b);
    if (av === null) return 1;
    if (bv === null) return -1;
    if (av === bv) return byName(a, b);
    return direction === 'asc' ? av - bv : bv - av;
  };

  const comparators: Record<
    CohortSortField,
    (a: CohortPatientOverview, b: CohortPatientOverview) => number
  > = {
    name: byName,
    hypo: worstFirst(r => r.hypoCount, 'desc'),
    inTarget: worstFirst(r => r.inTargetPercent, 'asc'),
    compliance: worstFirst(r => r.compliancePercent, 'asc'),
    insulin: worstFirst(r => r.averageDailyUnits, 'desc'),
    hba1c: worstFirst(r => r.latestHba1c, 'desc'),
    // A patient who has never recorded is the extreme of "longest ago", so
    // null sorts first here rather than last.
    lastEntry: (a, b) => {
      const av = a.lastEntryDate === null ? Number.MAX_SAFE_INTEGER : a.daysSinceEntry ?? 0;
      const bv = b.lastEntryDate === null ? Number.MAX_SAFE_INTEGER : b.daysSinceEntry ?? 0;
      return av === bv ? byName(a, b) : bv - av;
    }
  };

  return [...rows].sort(comparators[field]);
}

/** Case-insensitive match on name or control place. */
export function filterCohort(
  rows: CohortPatientOverview[],
  query: string
): CohortPatientOverview[] {
  const needle = query.trim().toLowerCase();
  if (!needle) return rows;
  return rows.filter(
    row =>
      (row.fullName ?? '').toLowerCase().includes(needle) ||
      (row.controlPlace ?? '').toLowerCase().includes(needle)
  );
}

/** Maps a snake_case doctor_patient_overview row. */
export function mapOverviewRow(row: any): CohortPatientOverview {
  const num = (v: unknown) => (v === null || v === undefined ? null : Number(v));
  return {
    patientId: row.patient_id,
    fullName: row.full_name ?? null,
    birthDate: row.birth_date ?? null,
    controlPlace: row.control_place ?? null,
    windowDays: Number(row.window_days),
    windowStart: row.window_start,
    lastEntryDate: row.last_entry_date ?? null,
    daysSinceEntry: num(row.days_since_entry),
    daysWithEntry: Number(row.days_with_entry ?? 0),
    compliancePercent: Number(row.compliance_percent ?? 0),
    readings: Number(row.readings ?? 0),
    glucoseAverage: num(row.glucose_average),
    glucoseMinimum: num(row.glucose_minimum),
    glucoseMaximum: num(row.glucose_maximum),
    hypoCount: Number(row.hypo_count ?? 0),
    hyperCount: Number(row.hyper_count ?? 0),
    inTargetPercent: num(row.in_target_percent),
    lastHypoDate: row.last_hypo_date ?? null,
    injections: Number(row.injections ?? 0),
    insulinUnits: Number(row.insulin_units ?? 0),
    averageDailyUnits: Number(row.average_daily_units ?? 0),
    distinctSites: Number(row.distinct_sites ?? 0),
    lipodystrophyCount: Number(row.lipodystrophy_count ?? 0),
    latestHba1c: num(row.latest_hba1c),
    latestHba1cDate: row.latest_hba1c_date ?? null
  };
}

/** "17/03" — the chart axis, where a full date will not fit. */
function shortDate(date: string): string {
  return `${date.slice(8, 10)}/${date.slice(5, 7)}`;
}

/** Rows for the daily glucose TrendChart. */
export function progressGlucoseData(
  progress: PatientProgress
): { date: string; average: number; minimum: number; maximum: number }[] {
  return progress.glucose.daily.map(day => ({
    date: shortDate(day.date),
    average: Number(day.average ?? 0),
    minimum: Number(day.minimum ?? 0),
    maximum: Number(day.maximum ?? 0)
  }));
}

/** Rows for the daily insulin TrendChart. */
export function progressInsulinData(
  progress: PatientProgress
): { date: string; units: number }[] {
  return progress.insulin.daily.map(day => ({
    date: shortDate(day.date),
    units: Number(day.units ?? 0)
  }));
}

/** Rows for the HbA1c TrendChart — trailing history, not just the window. */
export function progressHba1cData(
  progress: PatientProgress
): { date: string; hba1c: number }[] {
  return progress.clinical.hba1cTrend.map(point => ({
    date: shortDate(point.date),
    hba1c: Number(point.hba1c)
  }));
}

/** Rows for the injection-site CategoryChart, most-used first. */
export function progressSiteData(
  progress: PatientProgress
): { site: string; count: number }[] {
  return progress.sites.mostUsed.map((use: ReportSiteUse) => ({
    site: formatSiteUse(use),
    count: use.count
  }));
}

/** Rows for the input-frequency CategoryChart — days recorded, per week. */
export function progressFrequencyData(
  progress: PatientProgress
): { week: string; days: number; entries: number }[] {
  return progress.frequency.weekly.map(week => ({
    week: shortDate(week.weekStart),
    days: week.days,
    entries: week.entries
  }));
}
